// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * FirmwareUpdateContext — thin DB-polling wrapper.
 *
 * All deployment state lives in the backend `firmware_deployment_statuses`
 * table. This context polls it every few seconds and exposes the result.
 * No browser-side progress/status tracking — a page refresh picks up where
 * things left off.
 */

import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from '../utils/axiosConfig';
import { isMaintenance } from '../utils/maintenanceMode';
import backgroundFirmwareApi from '../services/backgroundFirmwareApi';
import { useAuth } from './AuthContext';

const FirmwareUpdateContext = createContext(null);

export const useFirmwareUpdate = () => {
  const ctx = useContext(FirmwareUpdateContext);
  if (!ctx) throw new Error('useFirmwareUpdate must be used within FirmwareUpdateProvider');
  return ctx;
};

const POLL_INTERVAL = 3000;
const ACTIVE_STATUSES = new Set([
  'pending',
  'initializing',
  'uploading',
  'updating',
  'completing',
  'rebooting',
  'retrying',
]);

// Devices the user has explicitly cleared/dismissed. The backend keeps (and a
// finishing background push task can re-write) terminal "completed/failed" rows,
// so a plain DELETE isn't enough — the 3s poll would just bring them back. We
// remember dismissed device ids client-side (persisted across reloads) and hide
// their terminal rows. The moment a device starts a NEW deployment (an active
// status), it is automatically un-dismissed so real progress is never hidden.
const DISMISSED_KEY = 'firmwareDismissedDeviceIds';

const loadDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw).map(String) : []);
  } catch (_) {
    return new Set();
  }
};

const saveDismissed = (set) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch (_) {
    /* ignore */
  }
};

export const FirmwareUpdateProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [firmwareFile, setFirmwareFileState] = useState(null);
  const [selectedStargates, setSelectedStargatesRaw] = useState(new Set());
  const setSelectedStargates = useCallback((val) => {
    setSelectedStargatesRaw(val instanceof Set ? val : new Set(val));
  }, []);
  const [deploymentStatuses, setDeploymentStatuses] = useState({});
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const dismissedRef = useRef(loadDismissed());
  const deploymentStatusesRef = useRef({});
  useEffect(() => {
    deploymentStatusesRef.current = deploymentStatuses;
  }, [deploymentStatuses]);

  // Restore cached file reference on mount (display only)
  useEffect(() => {
    (async () => {
      try {
        const fileCache = (await import('../services/fileCache')).default;
        const cached = await fileCache.getFile();
        if (cached && mountedRef.current) setFirmwareFileState(cached);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setFirmwareFile = useCallback(async (file) => {
    setFirmwareFileState(file);
    try {
      const fileCache = (await import('../services/fileCache')).default;
      if (file) await fileCache.storeFile(file);
      else await fileCache.clearFile();
    } catch (_) {
      /* ignore */
    }
  }, []);

  // ---------- Polling ----------

  // Drop rows the user dismissed/cleared. A device that begins a NEW deployment
  // (any active status) is automatically un-dismissed so live progress shows.
  const applyDismissFilter = useCallback((statuses) => {
    if (dismissedRef.current.size === 0) return statuses;
    const out = {};
    let changed = false;
    for (const [id, s] of Object.entries(statuses)) {
      if (!s) continue;
      const key = String(id);
      if (dismissedRef.current.has(key)) {
        if (ACTIVE_STATUSES.has(s.status)) {
          dismissedRef.current.delete(key);
          changed = true;
          out[key] = s;
        }
        // else: still dismissed + terminal → hide it
      } else {
        out[key] = s;
      }
    }
    if (changed) saveDismissed(dismissedRef.current);
    return out;
  }, []);

  const fetchStatuses = useCallback(async () => {
    // Pause while a restore is swapping the databases (backend is down).
    if (isMaintenance()) return;
    try {
      const data = await backgroundFirmwareApi.getDeploymentStatuses({
        includeInactive: true,
      });
      if (mountedRef.current && data && data.statuses) {
        const filtered = applyDismissFilter(data.statuses);
        // Only update state when the statuses actually changed. The poll runs
        // every 3s app-wide; blindly setting a fresh object each time re-rendered
        // the entire app on every tick (the common idle case has no changes).
        if (JSON.stringify(filtered) !== JSON.stringify(deploymentStatusesRef.current)) {
          setDeploymentStatuses(filtered);
        }
      }
      // When data is null (auth error / network error), keep the last
      // known statuses so the UI doesn't flash empty during session gaps.
    } catch (_) {
      /* ignore */
    }
  }, [applyDismissFilter]);

  useEffect(() => {
    // Only poll while authenticated. Polling after a session timeout/logout
    // just produces a flood of 401s in the console (the provider is mounted
    // app-wide, including on the login page).
    if (!currentUser) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    fetchStatuses();
    pollRef.current = setInterval(fetchStatuses, POLL_INTERVAL);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [fetchStatuses, currentUser]);

  // Re-poll immediately when the user logs back in so the UI refreshes
  // without waiting for the next 3-second tick.
  useEffect(() => {
    const onLogin = () => {
      fetchStatuses();
    };
    window.addEventListener('login', onLogin);
    return () => window.removeEventListener('login', onLogin);
  }, [fetchStatuses]);

  // ---------- Derived state ----------

  const hasActiveOperations = useCallback(() => {
    return Object.values(deploymentStatuses).some((s) => s && ACTIVE_STATUSES.has(s.status));
  }, [deploymentStatuses]);

  const activeUploads = new Set(
    Object.entries(deploymentStatuses)
      .filter(([, s]) => s && ACTIVE_STATUSES.has(s.status))
      .map(([id]) => parseInt(id))
  );

  const updateInProgress = activeUploads.size > 0;

  // Sync active-operation state to localStorage so the auth layer
  // (axiosConfig 401 handler + AuthContext inactivity timer) can
  // detect in-progress firmware work and try a token refresh instead
  // of an immediate session logout.
  useEffect(() => {
    if (updateInProgress) {
      localStorage.setItem(
        'firmwareUpdateState',
        JSON.stringify({
          updateInProgress: true,
          activeUploads: [...activeUploads],
        })
      );
    } else {
      localStorage.removeItem('firmwareUpdateState');
    }
  }, [updateInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backwards-compat: expose updateStatus shaped like { [id]: { status, message } }
  const updateStatus = {};
  for (const [id, s] of Object.entries(deploymentStatuses)) {
    if (s) updateStatus[id] = { status: s.status, message: s.message };
  }

  // ---------- Optimistic UI ----------

  const setOptimisticStatuses = useCallback((deviceMap) => {
    // Starting a fresh update clears any prior dismissal for those devices.
    let changed = false;
    for (const sgId of Object.keys(deviceMap)) {
      if (dismissedRef.current.delete(String(sgId))) changed = true;
    }
    if (changed) saveDismissed(dismissedRef.current);
    setDeploymentStatuses((prev) => {
      const next = { ...prev };
      for (const [sgId, entry] of Object.entries(deviceMap)) {
        next[String(sgId)] = { ...entry };
      }
      return next;
    });
  }, []);

  // Hide a single finished device without wiping everyone else's rows. The row
  // stays hidden (even across the poll + reloads) until that device starts a
  // new deployment.
  const dismissDevice = useCallback((sgId) => {
    const key = String(sgId);
    dismissedRef.current.add(key);
    saveDismissed(dismissedRef.current);
    setDeploymentStatuses((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // ---------- Actions ----------

  const cancelDevice = useCallback(
    async (ip) => {
      try {
        await axios.post('/firmware/push/cancel', { ip }, { timeout: 15000 });
      } catch (_) {
        /* ignore */
      }
      fetchStatuses();
    },
    [fetchStatuses]
  );

  const cancelAll = useCallback(async () => {
    // Suppress every device currently shown so a finishing background task or
    // an in-flight poll can't restore the rows after the user clears them.
    for (const id of Object.keys(deploymentStatusesRef.current)) {
      dismissedRef.current.add(String(id));
    }
    saveDismissed(dismissedRef.current);
    try {
      await axios.post('/firmware/push/cancel-all', null, { timeout: 30000 });
    } catch (_) {
      /* ignore */
    }
    try {
      await backgroundFirmwareApi.cancelAllBackgroundUploads();
    } catch (_) {
      /* ignore */
    }
    try {
      await backgroundFirmwareApi.clearDeploymentStatuses();
    } catch (_) {
      /* ignore */
    }
    setDeploymentStatuses({});
  }, []);

  const rebootDevice = useCallback(
    async (ip, options = {}) => {
      try {
        await axios.post(
          '/firmware/push/reboot',
          { ip, confirm_reboot_risk: Boolean(options.confirmRebootRisk) },
          { timeout: 30000 }
        );
      } finally {
        fetchStatuses();
      }
    },
    [fetchStatuses]
  );

  const value = {
    firmwareFile,
    selectedStargates,
    deploymentStatuses,
    updateInProgress,
    activeUploads,
    updateStatus,

    setFirmwareFile,
    setSelectedStargates,
    setOptimisticStatuses,
    hasActiveOperations,
    cancelDevice,
    cancelAll,
    dismissDevice,
    rebootDevice,
    refreshStatuses: fetchStatuses,
  };

  return <FirmwareUpdateContext.Provider value={value}>{children}</FirmwareUpdateContext.Provider>;
};
